import {
  locationApi,
  positionApi,
  scheduleApi,
  roleApi,
  employeeApi,
  companySettingsApi,
} from '../services/api';
import type { QaCheckRow, QaResultStatus } from './timecardScenarios/types';

type HealthDef = {
  id: string;
  title: string;
  run: () => Promise<{ status: QaResultStatus; message: string }>;
};

function nonempty(label: string, rows: unknown): { status: QaResultStatus; message: string } {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return { status: 'failed', message: `${label} returned 0 records` };
  }
  return { status: 'passed', message: `${label}: ${list.length} record(s)` };
}

export const HEALTH_CHECK_DEFS: HealthDef[] = [
  {
    id: 'HEALTH-LOCATIONS',
    title: 'Locations list is not empty',
    run: async () => nonempty('Locations', await locationApi.getAll()),
  },
  {
    id: 'HEALTH-POSITIONS',
    title: 'Positions list is not empty',
    run: async () => nonempty('Positions', await positionApi.getAll()),
  },
  {
    id: 'HEALTH-SCHEDULES',
    title: 'Schedules list is not empty',
    run: async () => nonempty('Schedules', await scheduleApi.getAll()),
  },
  {
    id: 'HEALTH-ROLES',
    title: 'Roles list is not empty',
    run: async () => nonempty('Roles', await roleApi.getAll()),
  },
  {
    id: 'HEALTH-EMPLOYEES',
    title: 'Employees list is not empty',
    run: async () => nonempty('Employees', await employeeApi.getAll({ includePayroll: false, presignImages: false })),
  },
  {
    id: 'HEALTH-COMPANY',
    title: 'Company settings load',
    run: async () => {
      const settings = await companySettingsApi.get();
      if (!settings) {
        return { status: 'failed', message: 'Company settings returned empty' };
      }
      const name = settings.companyName?.trim();
      return {
        status: 'passed',
        message: name ? `Company settings loaded (${name})` : 'Company settings loaded',
      };
    },
  },
];

export function healthRowsPending(): QaCheckRow[] {
  return HEALTH_CHECK_DEFS.map((d) => ({
    id: d.id,
    title: d.title,
    group: 'Health',
    status: 'pending',
    message: '',
  }));
}

export async function runHealthChecks(
  onRow: (row: QaCheckRow) => void
): Promise<QaCheckRow[]> {
  const results: QaCheckRow[] = [];
  for (const def of HEALTH_CHECK_DEFS) {
    onRow({ id: def.id, title: def.title, group: 'Health', status: 'running', message: '' });
    try {
      const result = await def.run();
      const row: QaCheckRow = {
        id: def.id,
        title: def.title,
        group: 'Health',
        status: result.status,
        message: result.message,
      };
      results.push(row);
      onRow(row);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      const row: QaCheckRow = {
        id: def.id,
        title: def.title,
        group: 'Health',
        status: 'failed',
        message,
      };
      results.push(row);
      onRow(row);
    }
  }
  return results;
}
